import axios from 'axios';
import {REDIRECT_STORAGE_KEY, SESSION_EXPIRED_KEY} from '../lib/authKeys';

const TOKEN_KEY = 'token';
type AccessTokenProvider = () => Promise<string>;
let accessTokenProvider: AccessTokenProvider | null = null;

// The auth0-spa-js `error` codes meaning the silent renewal can never succeed again without a new login.
const LOST_SESSION_CODES = new Set([
  'missing_refresh_token',
  'login_required',
  'invalid_grant',
  'consent_required'
]);

/** Whether a token-renewal failure means the Auth0 session is over, so only a new login can recover. */
export function isLostSessionError(error: unknown): boolean {
  const code = (error as {error?: unknown} | null | undefined)?.error;
  return typeof code === 'string' && LOST_SESSION_CODES.has(code);
}

// Several requests fail together (a bulk classify fires one per item); only the first redirects.
// The flag it sets is the guard: LoginPage clears it once the navigation lands.
function redirectToExpiredLogin(): void {
  if (sessionStorage.getItem(SESSION_EXPIRED_KEY) !== null) {
    return;
  }

  localStorage.removeItem(TOKEN_KEY);
  const {pathname, search} = globalThis.location;
  if (!pathname.endsWith('/login') && sessionStorage.getItem(REDIRECT_STORAGE_KEY) === null) {
    sessionStorage.setItem(REDIRECT_STORAGE_KEY, `${pathname}${search}`);
  }

  sessionStorage.setItem(SESSION_EXPIRED_KEY, '1');
  globalThis.location.assign(`${import.meta.env.BASE_URL}login`);
}

export function setAccessTokenProvider(provider: AccessTokenProvider | null): void {
  accessTokenProvider = provider;
}

/**
 An Axios instance that sends the signed-in user's bearer token and treats a 401 as a lost
 session. The PRI API's own instance is `api` below; the Data-owned reporting service accepts the
 same token at a different base path, so it gets a second instance from the same factory rather
 than a copy of the interceptors.
 */
export function createApiClient(baseURL: string) {
  const instance = axios.create({
    baseURL,
    // Indexes: null serializes array params as repeated bare keys (`vertical=A&vertical=B`), which
    // is what ASP.NET Core's query-string model binder expects for `string[]?` action parameters.
    paramsSerializer: {indexes: null}
  });

  instance.interceptors.request.use(async (config) => {
    let token: string | null;
    try {
      token =
        accessTokenProvider === null
          ? localStorage.getItem(TOKEN_KEY)
          : await accessTokenProvider();
    } catch (error: unknown) {
      // /auth/session is AuthContext's own probe: redirecting from it would loop a reload.
      if (isLostSessionError(error) && config.url?.includes('/auth/session') !== true) {
        redirectToExpiredLogin();
      }

      throw error;
    }

    if (token !== null && token !== '') {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
      recordApiError(error);

      // Session expired/invalid: drop the token and bounce back to sign-in.
      // Skip auto-redirect on sessionAuth failure so AuthContext can handle the error state without a bounce loop.
      const url = axios.isAxiosError(error) ? error.config?.url : undefined;
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 401 &&
        !url?.includes('/auth/session')
      ) {
        redirectToExpiredLogin();
      }

      throw error instanceof Error ? error : new Error('Request failed');
    }
  );

  return instance;
}

/**
 Shared Axios instance for the IW Investigations API. All requests go through the Vite dev-server
 `/api` proxy (see vite.config.ts), which Aspire service discovery points at the backend. Pair this
 with TanStack Query in feature hooks, e.g.
 `useQuery({queryKey: ['jobs'], queryFn: () => get('/jobs')})`.
 */
const api = createApiClient('/api');

// The most recent failed API call, kept in memory only so feedback submitted right afterwards can
// say what went wrong. Stale after LAST_ERROR_MAX_AGE_MS: an error from an hour ago is noise.
const LAST_ERROR_MAX_AGE_MS = 10 * 60 * 1000;
let lastApiError: {summary: string; at: number} | null = null;

/** One-line description of the last failed API call seen in the last 10 minutes, if any. */
export function getLastApiError(): string | undefined {
  if (lastApiError === null || Date.now() - lastApiError.at > LAST_ERROR_MAX_AGE_MS) {
    return undefined;
  }

  return lastApiError.summary;
}

function recordApiError(error: unknown): void {
  // An aborted query (navigation, superseded search) is not a failure the analyst could report.
  if (axios.isCancel(error)) {
    return;
  }

  if (!axios.isAxiosError(error) || error.config?.url?.startsWith('/feedback') === true) {
    return;
  }

  const now = new Date();
  // Path only - the query string can carry search terms and identifiers.
  const path = (error.config?.url ?? '').split('?')[0];
  const status = error.response?.status ?? 'network error';
  const method = (error.config?.method ?? 'get').toUpperCase();
  const summary = `${status} ${method} ${path}: ${getApiErrorMessage(error)} (${now.toISOString()})`;
  lastApiError = {summary: summary.slice(0, 1000), at: now.getTime()};
}

export default api;

export type UserInfoResponse = {
  username: string;
  display_name: string;
  role: string;
  allowed_verticals: string[] | null;
  skip_p0_approval: boolean;
  permissions: string[];
};
export type DevLoginResponse = {
  token: string;
  username: string;
  display_name: string;
  role: string;
  allowed_verticals: string[] | null;
  skip_p0_approval: boolean;
  permissions: string[];
};

/** POST /api/auth/session — provisions/loads the Auth0-authenticated application user. */
export const sessionAuth = async () => api.post<UserInfoResponse>('/auth/session');

/** POST /api/auth/logout — adds the current token's jti to the server-side blocklist. */
export const logoutApi = async () => api.post('/auth/logout');

/** POST /api/auth/dev-login — dev-only; returns a locally-signed JWT + user info. */
export const devLogin = async (email: string, displayName?: string) =>
  api.post<DevLoginResponse>('/auth/dev-login', {
    email,

    display_name: displayName
  });

/** RFC 7807 ProblemDetails shape returned by the API for error responses. */
export type ProblemDetails = {
  [key: string]: unknown;
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  errors?: Record<string, string[]>;
};

/**
 Extract a human-readable message from an arbitrary API/network error. Understands RFC 7807
 ProblemDetails (including ValidationProblemDetails `errors`), falling back to legacy `{ message }`
 payloads and the raw Error message.
 */
export function getApiErrorMessage(error: unknown, fallback = 'Something went wrong.'): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as ProblemDetails | {message?: string} | string | undefined;

    if (typeof data === 'string' && data.trim().length > 0) {
      return data;
    }

    if (typeof data === 'object' && data !== null) {
      const problem = data as ProblemDetails & {message?: string};

      if (problem.errors !== undefined) {
        const firstField = Object.values(problem.errors).find(
          (messages) => Array.isArray(messages) && messages.length > 0
        );
        const firstMessage = firstField?.[0];
        if (firstMessage !== undefined && firstMessage !== '') {
          return firstMessage;
        }
      }

      return problem.detail ?? problem.message ?? problem.title ?? fallback;
    }

    if (error.message !== '') {
      return error.message;
    }
  }

  if (error instanceof Error && error.message !== '') {
    return error.message;
  }

  return fallback;
}
